import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";
    const [pwd, setPwd] = useState("");
    const [pwd2, setPwd2] = useState("");
    const [show, setShow] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!token) {
            toast.error("Enlace inválido. Solicita uno nuevo.");
            return;
        }
        if (pwd.length < 6) {
            toast.error("La contraseña debe tener al menos 6 caracteres");
            return;
        }
        if (pwd !== pwd2) {
            toast.error("Las contraseñas no coinciden");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pwd });
            toast.success("Contraseña actualizada. Inicia sesión.");
            navigate("/login");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo restablecer la contraseña");
        } finally {
            setSubmitting(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md rounded-2xl border border-p2w-line bg-p2w-surface p-8 text-center">
                    <h1 className="font-sora font-extrabold text-2xl text-white">Enlace inválido</h1>
                    <p className="mt-2 text-p2w-gray text-sm">El enlace que usaste no es válido o está incompleto.</p>
                    <Link to="/forgot-password" className="mt-6 inline-block text-p2w-green hover:underline font-semibold">
                        Solicitar uno nuevo
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="reset-password-page">
            <div className="w-full max-w-md relative">
                <div className="glow-orb bg-p2w-green w-[300px] h-[300px] -top-20 -right-20 opacity-20" />
                <div className="relative rounded-2xl border border-p2w-line bg-p2w-surface p-8 shadow-glow-violet">
                    <div className="flex items-center gap-2 mb-2 text-p2w-green">
                        <ShieldCheck className="w-4 h-4" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-bold font-sora">Nueva contraseña</span>
                    </div>
                    <h1 className="font-sora font-extrabold text-3xl tracking-tight">Crea tu nueva contraseña</h1>
                    <p className="mt-1 text-p2w-gray text-sm">
                        Elige una contraseña fuerte. Mínimo 6 caracteres.
                    </p>

                    <form onSubmit={onSubmit} className="mt-6 space-y-4">
                        <div>
                            <Label className="text-p2w-gray text-xs uppercase tracking-widest">Nueva contraseña</Label>
                            <div className="relative mt-2">
                                <Input
                                    type={show ? "text" : "password"}
                                    data-testid="reset-password"
                                    value={pwd}
                                    onChange={(e) => setPwd(e.target.value)}
                                    minLength={6}
                                    required
                                    className="bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40 pr-12"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShow((s) => !s)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-p2w-gray hover:text-white"
                                    aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
                                >
                                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        <div>
                            <Label className="text-p2w-gray text-xs uppercase tracking-widest">Repetir contraseña</Label>
                            <Input
                                type={show ? "text" : "password"}
                                data-testid="reset-password-confirm"
                                value={pwd2}
                                onChange={(e) => setPwd2(e.target.value)}
                                minLength={6}
                                required
                                className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40"
                            />
                        </div>
                        <Button
                            type="submit"
                            data-testid="reset-submit"
                            disabled={submitting}
                            className="w-full bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 shadow-glow-green font-semibold py-6 rounded-xl"
                        >
                            {submitting ? "Guardando…" : "Restablecer contraseña"}
                        </Button>
                    </form>

                    <p className="mt-6 text-sm text-p2w-gray text-center">
                        <Link to="/login" className="text-p2w-green hover:underline font-semibold">
                            Volver a iniciar sesión
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
